/* eslint-disable import/order */
import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  ActivityIndicator, ScrollView, StyleSheet, Text, TextInput, Pressable, View,
} from 'react-native';
import Moment from 'moment';
import Modal from 'react-native-modal';
import Colors from '../constants/Colors';
import BinIcon24 from './24pxIcons/BinIcon24';
import CloseModalIcon from './CloseModalIcon';
import Style from '../constants/Style';
import addAssetNote from '../requests/Assets/AddAssetNote';
import editAssetNote from '../requests/Assets/EditAssetNote';
import deleteAssetNote from '../requests/Assets/DeleteAssetNote';
import { logEvent } from './LogEvent';
import { AssetNote } from '../constants/Types';
import isMobile from './IsMobile';
import PressableStyled from './PressableStyled';
import { FIELD_REQUIRED_MESSAGE } from '../constants/AlertMessages';
import { useSelector } from 'react-redux';

interface AddEditNoteProps {
  selectedNote: AssetNote,
  setEditNote: Dispatch<SetStateAction<boolean>>,
  assetId: string,
  editNote: boolean,
  setVisible: Dispatch<SetStateAction<boolean>>,
  setNotes: Dispatch<SetStateAction<AssetNote[]>>,
  notes: AssetNote[],
  modalVisible: boolean,
}

export default function AddEditNote({
  selectedNote, setEditNote, assetId, editNote, setVisible, setNotes, notes, modalVisible,
}: AddEditNoteProps) {
  const { firstName, lastName } = useSelector((state:
    { user: { firstName, lastName } }) => state.user);
  const [noteTitle, setNoteTitle] = useState<string>(selectedNote?.title);
  const [noteDescription, setNoteDescription] = useState<string>(selectedNote?.description);
  const [descriptionFocused, setDescriptionFocused] = useState<boolean>(false);
  const [titleFocused, setTitleFocused] = useState<boolean>(false);
  const [titleValid, setTitleValid] = useState<boolean>(true);
  const [descriptionValid, setDescriptionValid] = useState<boolean>(true);
  const [savingNote, setSavingNote] = useState<boolean>(false);

  const maxTitleLength: number = 50;

  const validateTitle: Function = (title: string) => {
    if (!title || title?.trim().length === 0 || title?.trim().length > maxTitleLength) {
      setTitleValid(false);
      return false;
    }
    setTitleValid(true);
    return true;
  };

  const validateDescription: Function = (text: string) => {
    if (text?.trim().length > 150) {
      setDescriptionValid(false);
      return false;
    }
    setDescriptionValid(true);
    return true;
  };

  const save: () => void = () => {
    logEvent('AddEditNote', 'save');
    const validTitle: string = validateTitle(noteTitle ?? selectedNote?.title);
    if (validTitle) {
      const validDescription: string = validateDescription(noteDescription);
      if (validDescription) {
        const title: string = (noteTitle ?? selectedNote?.title)?.trim();
        const description: string = (noteDescription ?? selectedNote?.description)?.trim();
        if (titleValid && selectedNote === null) {
          setSavingNote(true);
          addAssetNote(assetId, {
            Title: title,
            Description: description ?? '',
            name_value: `${firstName ?? ''} ${lastName ?? ''}`,
            Id: '',
          }).then((response) => {
            const newNote = {
              title,
              description,
              name: `${firstName ?? ''} ${lastName ?? ''}`,
              id: response.NoteId,
              createdOn: Moment().toISOString(),
              updatedOn: Moment().toISOString(),
            };
            setVisible(false);
            setNoteTitle(null);
            setNoteDescription(null);
            setSavingNote(false);
            setNotes([...notes, newNote]);
          });
        } else if (editNote) {
          setSavingNote(true);
          editAssetNote(assetId, {
            Title: title,
            Description: description,
            Id: selectedNote.id,
            name_value: selectedNote.name,
          }).then(() => {
            setNotes(notes.map((note: AssetNote) => ({
              ...note,
              ...(note.id === selectedNote.id
                ? { title, description, updatedOn: Moment().toISOString() } : {}),
            })));
            setNoteTitle(null);
            setNoteDescription(null);
            setSavingNote(false);
            setVisible(false);
            setEditNote(false);
          });
        } else {
          setSavingNote(false);
          setEditNote(true);
        }
      }
    }
  };

  const deleteNote: () => void = () => {
    logEvent('AddEditNote', 'delete_note');
    setSavingNote(true);
    const noteId: string = selectedNote.id;
    deleteAssetNote(assetId, noteId).then(() => {
      setSavingNote(false);
      setNotes(notes.filter((note: AssetNote) => note.id !== noteId));
      setVisible(false);
    });
  };

  const renderSaveCancel = () => (
    <View style={styles.buttonContainer}>
      <PressableStyled
        style={styles.saveButton}
        onPress={save}
        testID='edit-note-modal-btn'
      >
        <Text style={styles.saveButtonText}>
          {editNote || selectedNote === null ? 'Save' : 'Edit'}
        </Text>
      </PressableStyled>
      <PressableStyled
        style={styles.cancelButton}
        onPress={() => {
          logEvent('AddEditNote', 'cancel');
          setNoteDescription(null);
          setVisible(false);
        }}
      >
        <Text style={styles.cancelButtonText}>
          {'Cancel'}
        </Text>
      </PressableStyled>
      {selectedNote !== null && (
        <View style={{ flex: 1, alignItems: 'flex-end' }}>
          <Pressable
            style={(state: { hovered: boolean, pressed: boolean }) => [
              styles.deleteButton, state.hovered && Style.hovered, state.pressed && Style.pressed,
            ]}
            onPress={deleteNote}
            testID='am-delete-asset-note-btn'
          >
            <BinIcon24 color={Colors.light.hereworksRed} />
          </Pressable>
        </View>
      )}
    </View>
  );

  const getTitleColor = () => {
    if (!titleValid) return Colors.light.hereworksUrgentRed;
    return titleFocused ? Colors.light.hereworksBlue300 : Colors.light.hereworksBlue100;
  };

  const getDescriptionColor = () => {
    if (!descriptionValid) return Colors.light.hereworksUrgentRed;
    return descriptionFocused ? Colors.light.hereworksBlue300 : Colors.light.hereworksBlue100;
  };

  return (
    <Modal
      isVisible={modalVisible}
      style={{ alignItems: 'center' }}
      animationIn={'fadeIn'}
      animationOut={'fadeOut'}
      propagateSwipe={false}
    >
      <View
        style={{
          alignItems: 'flex-start',
          justifyContent: 'center',
          borderRadius: 10,
          backgroundColor: '#fff',
          padding: 24,
          alignSelf: isMobile ? 'stretch' : 'center',
          width: isMobile ? undefined : 532,
        }}
      >
        <PressableStyled
          onPress={() => {
            logEvent('AddEditNote', 'close_modal');
            setVisible(false);
          }}
          hitSlop={{
            top: 20,
            bottom: 20,
            left: 40,
            right: 40,
          }}
          style={Style.closeButton}
          testID='am-close-asset-note-modal-btn'
        >
          <CloseModalIcon />
        </PressableStyled>
        {selectedNote && !editNote && <>
          <Text style={styles.heading} numberOfLines={2}>
            {selectedNote?.title}
          </Text>
          <ScrollView style={{ maxHeight: 300 }}>
            <Text style={styles.description}>{selectedNote?.description}</Text>
          </ScrollView>
          <Text style={styles.dateAndName}>
            {`${Moment(selectedNote?.updatedOn).format('DD.MM.YYYY')} by `}
            {selectedNote?.name}
          </Text>
        </>}
        {(!selectedNote || editNote) && <>
          <Text style={[styles.heading, { textAlign: 'center' }]}>
            {selectedNote ? 'Edit note' : 'Add a note'}
          </Text>
          <Text style={styles.textBoxHeaders}>{'Title'}</Text>
          <TextInput
            style={[
              styles.inputText,
              { borderColor: getTitleColor() },
            ]}
            placeholderTextColor={Colors.light.hereworksBlack400}
            placeholder={'Enter title'}
            value={noteTitle ?? selectedNote?.title ?? ''}
            onSubmitEditing={(text) => {
              setNoteTitle(text.nativeEvent.text);
            }}
            onFocus={() => setTitleFocused(true)}
            onBlur={() => setTitleFocused(false)}
            onChangeText={(text: string) => {
              validateTitle(text);
              setNoteTitle(text);
            }}
          />
          {noteTitle?.trim().length > maxTitleLength && (
            <Text style={[styles.validationText, { color: Colors.light.hereworksUrgentRed }]}>
              {`${noteTitle?.trim().length ?? selectedNote?.title?.trim().length ?? 0}/`}
              {maxTitleLength}
            </Text>
          )}
          {(noteTitle ?? '').trim().length === 0 && !titleValid && (
            <Text style={[styles.validationText, { color: Colors.light.hereworksUrgentRed }]}>
              {FIELD_REQUIRED_MESSAGE}
            </Text>
          )}
          <Text style={styles.textBoxHeaders}>{'Description'}</Text>
          <TextInput
            testID='add-asset-note-desc-input-field'
            style={[styles.inputText, { borderColor: getDescriptionColor() }]}
            placeholderTextColor={Colors.light.hereworksBlack400}
            placeholder={'Enter description'}
            value={noteDescription ?? selectedNote?.description ?? ''}
            onSubmitEditing={({ nativeEvent: { text } }: { nativeEvent: { text: string } }) => {
              setNoteDescription(text);
            }}
            onFocus={() => setDescriptionFocused(true)}
            onBlur={() => {
              setDescriptionFocused(false);
            }}
            onChangeText={(text: string) => {
              validateDescription(text);
              setNoteDescription(text);
            }}
          />
          <Text
            style={[
              styles.validationText,
              noteDescription?.trim().length > 150
                ? { color: Colors.light.hereworksUrgentRed }
                : {},
            ]}
          >
            {`${noteDescription?.trim().length
              ?? selectedNote?.description?.trim().length ?? 0}/150`}
          </Text>
        </>}
        {renderSaveCancel()}
        {savingNote && (
          <View style={styles.loadingIcon}>
            <ActivityIndicator color={Colors.light.hereworksLoading} size={'large'} />
          </View>
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  textBoxHeaders: {
    marginTop: 12,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  validationText: {
    marginTop: 4,
    color: Colors.light.hereworksBlack300,
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 20,
  },
  heading: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 28,
    lineHeight: 42,
    marginBottom: 32,
    color: Colors.light.hereworksBlack600,
    alignSelf: 'stretch',
    zIndex: -1,
  },
  description: {
    color: Colors.light.hereworksBlack600,
    alignSelf: 'stretch',
    fontFamily: 'Roboto_400Regular',
    alignItems: 'center',
    fontSize: 16,
    lineHeight: 24,
  },
  inputText: {
    color: Colors.light.hereworksBlack600,
    alignSelf: 'stretch',
    fontFamily: 'Roboto_400Regular',
    alignItems: 'center',
    fontSize: 16,
    backgroundColor: Colors.light.hereworksWhite300,
    borderWidth: 1,
    alignContent: 'center',
    marginVertical: 5,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 5,
    verticalAlign: 'top',
    outlineWidth: 0,
  },
  dateAndName: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 14,
    color: Colors.light.hereworksBlack400,
    marginTop: 8,
  },
  buttonContainer: {
    marginTop: 40,
    flexDirection: 'row',
    alignSelf: 'stretch',
  },
  saveButton: {
    backgroundColor: Colors.light.hereworksLightBlue,
    borderRadius: 4,
    marginRight: 12,
  },
  saveButtonText: {
    color: '#fff',
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    marginVertical: 8,
    marginHorizontal: 24,
  },
  cancelButton: {
    backgroundColor: '#fff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksLightBlue,
  },
  cancelButtonText: {
    color: Colors.light.hereworksLightBlue,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    marginVertical: 8,
    marginHorizontal: 24,
  },
  deleteButton: {
    height: 40,
    width: 40,
    borderColor: Colors.light.hereworksRed,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingIcon: {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    borderRadius: 8,
    position: 'absolute',
    justifyContent: 'center',
    backgroundColor: '#00000020',
  },
});
